import React from 'react';
import { Dropdown } from 'react-bootstrap';
import styled from 'styled-components';

// Define types for the props
interface GenericDropdownProps {
  label: string;  // Label text for the dropdown
  options: { value: string; label: string }[];  // Options array with value and label
  value: any;  // Selected value
  onChange: (value: any) => void;  // Change handler
  disabled?: boolean; 
}

// Styled component for the dropdown container
const DropdownWrapper = styled.div`
  width: 100%;  // Make the wrapper take up full width
  min-width: 200px;  // Set a minimum width for larger screens
  margin-bottom: 1rem;  // Space between dropdowns
  position: relative;
  overflow: visible;
  z-index: 1;

  &:focus-within {
    z-index: 4500;
  }

  .dropdown.show {
    z-index: 4500;
  }

  @media (max-width: 576px) {
    min-width: 100%;  // Full width on mobile devices
  }
`;

// Styled component for the dropdown label
const DropdownLabel = styled.label`
  font-size: 1rem;
  font-weight: 600;
  color: #000;  // Black text for the label
  margin-bottom: 0.5rem;
  display: block;  // Make sure label appears above the dropdown
`;

const StyledDropdownMenu = styled(Dropdown.Menu)`
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #d6dee7;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.16);
  z-index: 3000 !important;
`;

// Styled component for the dropdown toggle button (neutral style)
const DropdownToggleButton = styled(Dropdown.Toggle)<{ disabled?: boolean }>`
  && {
  width: 100%;  // Make the toggle button span full width
  text-align: left;  // Align the text to the left for better readability
  padding: 10px 12px;  // Adjust padding for consistency on mobile
  background-color: #f8f9fa !important;  // Neutral light background
  border: 1px solid #ccc !important;  // Subtle border
  color: #1f2937 !important;  // Force readable text color
  box-shadow: none;  // Remove shadow for a more neutral design
  opacity: 1 !important;
  ${({ disabled }) => disabled && 'background-color: #e9ecef !important; pointer-events: none;'}
  }

  &:focus {
    border-color: #aaa !important;  // Slightly darker border on focus
    box-shadow: none !important;  // Remove default focus shadow
  }

  &:hover {
    background-color: #e9ecef !important;  // Slight change on hover for user interaction feedback
  }

  &::after {
    border-top-color: #6b7280;
  }
`;

const GenericDropdown: React.FC<GenericDropdownProps> = ({ label, options, value, onChange, disabled }) => {
  return (
    <DropdownWrapper>
      {/* Dropdown Label */}
      <DropdownLabel>{label}</DropdownLabel>

      <Dropdown onSelect={(selectedValue) => onChange(selectedValue as string)} style={{ width: '100%' }}>
        {/* Custom neutral Dropdown.Toggle button */}
        <DropdownToggleButton 
          id="dropdown-custom-components"
          value={value}
          disabled={disabled}>
          {options.find(option => option.value === value)?.label || 'Select an option'}
        </DropdownToggleButton>

        <StyledDropdownMenu>
          {options.map(option => (
            <Dropdown.Item key={option.value} eventKey={option.value}>
              {option.label}
            </Dropdown.Item>
          ))}
        </StyledDropdownMenu>
      </Dropdown>
    </DropdownWrapper>
  );
}

export default GenericDropdown;
